/* =====================================================================
   <hm-announcement-rotator>
   Mobile-only single-row carousel for the announcement bar. Cycles its
   .announcement-bar-mobile__slide children (review slide + message
   slides) one at a time. Desktop/tablet use the 3-column overlay and
   keep this element display:none, so the rotator only runs on phones.

   Self-contained — does not touch the base theme's <announcement-bar>
   component or theme.js. Respects the section autoplay setting, pauses on
   hover/focus/touch hold and when the tab is hidden, and respects
   prefers-reduced-motion.
   ===================================================================== */

if (!customElements.get('hm-announcement-rotator')) {
  customElements.define(
    'hm-announcement-rotator',
    class HmAnnouncementRotator extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.announcement-bar-mobile__slide'));
        this.speed = (parseInt(this.getAttribute('cycle-speed'), 10) || 5) * 1000;
        this.hoverPaused = false;
        this.focusPaused = false;
        this.pointerPaused = false;

        const activeIndex = this.slides.findIndex((slide) => slide.hasAttribute('data-active'));
        this.index = activeIndex === -1 ? 0 : activeIndex;
        if (activeIndex === -1 && this.slides[0]) {
          this.slides[0].setAttribute('data-active', '');
        }

        this.mobileQuery = window.matchMedia('(max-width: 740px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.mobileQuery.addEventListener('change', this.sync);
        this.reducedMotion.addEventListener('change', this.sync);
        document.addEventListener('visibilitychange', this.onVisibility);
        window.addEventListener('load', this.updateHeight);

        this.addEventListener('pointerenter', this.onPointerEnter);
        this.addEventListener('pointerleave', this.onPointerLeave);
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointerup', this.onPointerEnd);
        this.addEventListener('pointercancel', this.onPointerEnd);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);

        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.updateHeight);
          this.resizeObserver.observe(this);
        }

        this.sync();
        window.requestAnimationFrame(this.updateHeight);
      }

      disconnectedCallback() {
        this.stop();
        this.mobileQuery.removeEventListener('change', this.sync);
        this.reducedMotion.removeEventListener('change', this.sync);
        document.removeEventListener('visibilitychange', this.onVisibility);
        window.removeEventListener('load', this.updateHeight);
        this.removeEventListener('pointerenter', this.onPointerEnter);
        this.removeEventListener('pointerleave', this.onPointerLeave);
        this.removeEventListener('pointerdown', this.onPointerDown);
        this.removeEventListener('pointerup', this.onPointerEnd);
        this.removeEventListener('pointercancel', this.onPointerEnd);
        this.removeEventListener('focusin', this.onFocusIn);
        this.removeEventListener('focusout', this.onFocusOut);

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      // Run only when it makes sense: on phones, more than one slide,
      // autoplay enabled, motion allowed, and the tab is visible.
      sync = () => {
        this.updateHeight();

        const shouldRun =
          this.hasAttribute('auto-play') &&
          this.mobileQuery.matches &&
          !this.reducedMotion.matches &&
          this.slides.length > 1 &&
          !document.hidden &&
          !this.hoverPaused &&
          !this.focusPaused &&
          !this.pointerPaused;

        shouldRun ? this.start() : this.stop();
      };

      onVisibility = () => {
        document.hidden ? this.stop() : this.sync();
      };

      start = () => {
        this.stop();
        this.timer = window.setInterval(this.tick, this.speed);
      };

      stop = () => {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      };

      tick = () => {
        if (this.slides[this.index]) {
          this.slides[this.index].removeAttribute('data-active');
        }
        this.index = (this.index + 1) % this.slides.length;
        if (this.slides[this.index]) {
          this.slides[this.index].setAttribute('data-active', '');
        }
      };

      onPointerEnter = (event) => {
        if (event.pointerType === 'mouse') {
          this.hoverPaused = true;
          this.sync();
        }
      };

      onPointerLeave = (event) => {
        if (event.pointerType === 'mouse') {
          this.hoverPaused = false;
          this.sync();
        }
      };

      onPointerDown = (event) => {
        this.pointerPaused = true;
        this.sync();
      };

      onPointerEnd = () => {
        this.pointerPaused = false;
        this.sync();
      };

      onFocusIn = (event) => {
        this.focusPaused = true;
        this.sync();
      };

      onFocusOut = () => {
        this.focusPaused = false;
        this.sync();
      };

      updateHeight = () => {
        if (!this.mobileQuery || !this.mobileQuery.matches) {
          return;
        }

        const section = this.closest('[id^="shopify-section-"]');
        const height = section ? section.clientHeight : this.clientHeight;
        document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
      };
    }
  );
}
